import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check, Copy } from "lucide-react";
import { CONFIG } from "../../lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LemFi brand palette
const PURPLE = "#4C1D95";
const GREEN = "#22C55E";

export default function Lemfi() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [issuedCode, setIssuedCode] = useState(CONFIG.promoCode);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nom et email requis");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/lemfi/leads`, form);
      if (data?.promo_code) setIssuedCode(data.promo_code);
      setDone(true);
      toast.success("C'est noté ! Ton code promo est envoyé par email.");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessaie.");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(issuedCode);
    setCopied(true);
    toast.success("Code copié : " + issuedCode);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      id="lemfi"
      data-testid="lemfi-section"
      className="relative overflow-hidden py-24 lg:py-40"
      style={{ backgroundColor: PURPLE }}
    >
      {/* soft brand glows (solid, no red) */}
      <div
        className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full opacity-30 blur-[120px]"
        style={{ backgroundColor: GREEN }}
      />
      <div
        className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full opacity-20 blur-[120px]"
        style={{ backgroundColor: "#7C3AED" }}
      />

      <div className="relative mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-5 lg:grid-cols-12 lg:px-10">
        {/* Left: offer */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="mb-6 inline-flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 font-mono-x text-xs uppercase tracking-[0.3em] text-white/80 ring-1 ring-white/15"
          >
            <span style={{ color: GREEN }}>Powered by</span>
            <span className="font-display text-lg tracking-normal text-white">LemFi</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="font-display text-5xl uppercase leading-[0.88] tracking-tight text-white lg:text-7xl"
          >
            <span style={{ color: GREEN }}>-30%</span> sur<br />ton billet
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-8 space-y-6 text-white/80"
          >
            <p className="max-w-lg text-base leading-relaxed lg:text-lg">
              Inscris-toi sur <span className="font-semibold text-white">LemFi</span> avec le code
              promo <span className="font-mono-x font-semibold" style={{ color: GREEN }}>ECLIPSE</span> et
              profite de <span className="font-semibold text-white">-30%</span> sur ton billet Éclipse.
            </p>

            <ol className="space-y-3 font-mono-x text-sm text-white/70">
              <li className="flex gap-3">
                <span style={{ color: GREEN }}>01</span> Crée ton compte LemFi
              </li>
              <li className="flex gap-3">
                <span style={{ color: GREEN }}>02</span> Utilise le code{" "}
                <span className="text-white">ECLIPSE</span>
              </li>
              <li className="flex gap-3">
                <span style={{ color: GREEN }}>03</span> Laisse-nous un message ici → ton code promo
                billet est généré
              </li>
            </ol>

            <a
              href={CONFIG.lemfiUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lemfi-external-link"
              className="group mt-2 inline-flex items-center gap-2 px-8 py-4 font-mono-x text-sm font-semibold uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-[1.02]"
              style={{ backgroundColor: GREEN }}
            >
              S’inscrire sur LemFi
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        {/* Right: form / code */}
        <div className="lg:col-span-5 lg:col-start-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="rounded-2xl bg-white p-8 shadow-2xl lg:p-10"
          >
            {!done ? (
              <form onSubmit={submit} data-testid="lemfi-form" className="space-y-5">
                <h3 className="font-display text-2xl uppercase tracking-tight text-zinc-900">
                  Laisse-nous un message
                </h3>
                <p className="text-sm text-zinc-500">
                  Une fois inscrit sur LemFi, remplis ce formulaire pour débloquer ton code promo
                  billet.
                </p>

                <div>
                  <label className="mb-2 block font-mono-x text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    Nom
                  </label>
                  <input
                    data-testid="lemfi-input-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-900 outline-none transition-colors duration-300 placeholder:text-zinc-400 focus:border-[#4C1D95]"
                    placeholder="Ton nom"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono-x text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    Email
                  </label>
                  <input
                    data-testid="lemfi-input-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-900 outline-none transition-colors duration-300 placeholder:text-zinc-400 focus:border-[#4C1D95]"
                    placeholder="ton@email.com"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono-x text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    Message (optionnel)
                  </label>
                  <textarea
                    data-testid="lemfi-input-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    className="w-full resize-none rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-900 outline-none transition-colors duration-300 placeholder:text-zinc-400 focus:border-[#4C1D95]"
                    placeholder="Dis-nous tout..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="lemfi-submit"
                  disabled={loading}
                  className="w-full rounded-lg px-6 py-4 font-mono-x text-sm font-semibold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.01] disabled:opacity-60"
                  style={{ backgroundColor: PURPLE }}
                >
                  {loading ? "Envoi..." : "Débloquer mon code promo"}
                </button>
              </form>
            ) : (
              <div data-testid="lemfi-success" className="space-y-6 text-center">
                <div
                  className="mx-auto flex h-14 w-14 items-center justify-center rounded-full"
                  style={{ backgroundColor: "#DCFCE7" }}
                >
                  <Check className="h-7 w-7" style={{ color: GREEN }} />
                </div>
                <h3 className="font-display text-2xl uppercase tracking-tight text-zinc-900">
                  Ton code promo unique
                </h3>
                <p className="text-sm text-zinc-500">
                  Il vient aussi de t’être envoyé par email. Utilise-le au moment d’acheter ton
                  billet pour appliquer ta réduction.
                </p>
                <button
                  onClick={copyCode}
                  data-testid="lemfi-copy-code"
                  className="group mx-auto flex w-full items-center justify-between rounded-lg border border-dashed px-6 py-4"
                  style={{ borderColor: PURPLE, backgroundColor: "#F5F3FF" }}
                >
                  <span className="font-display text-2xl tracking-[0.15em]" style={{ color: PURPLE }}>
                    {issuedCode}
                  </span>
                  {copied ? (
                    <Check className="h-5 w-5" style={{ color: GREEN }} />
                  ) : (
                    <Copy className="h-5 w-5 text-zinc-400 transition-colors group-hover:text-[#4C1D95]" />
                  )}
                </button>
                <a
                  href={CONFIG.weezeventUrl}
                  data-testid="lemfi-buy-after"
                  className="inline-block font-mono-x text-xs uppercase tracking-[0.2em] text-zinc-600 link-underline"
                >
                  Acheter mon billet →
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
